import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { userAPI } from '@/lib/api';

// 定义用户角色类型
export type UserRole = 'user' | 'admin' | 'merchant' | 'merchant_admin' | 'super_admin' | 'operator' | 'community_leader' | 'organization_leader';

// 定义用户信息类型
interface UserInfo {
  id: string;
  username: string;
  email: string;
  phone?: string;
  nickname?: string;
  role: UserRole;
  isAuthenticated: boolean;
  realNameVerified: boolean; // 实名认证状态
  avatar?: string;
  /** 微信 openid，授权登录后由后端返回并保存 */
  wechatOpenid?: string;
  communityRoles?: {
    [communityId: string]: 'member' | 'admin' | 'leader'
  };
  organizations?: Array<{
    id: string;
    name: string;
    isPrimary: boolean;
    status: 'active' | 'pending' | 'suspended';
    logo?: string;
    shortName?: string;
  }>;
  currentOrganization?: {
    id: string;
    name: string;
    isPrimary: boolean;
    status: 'active' | 'pending' | 'suspended' | 'approved' | 'rejected' | 'not_submitted';
    logo?: string;
    shortName?: string;
    merchantId?: string;
  };
}

// 定义上下文类型
interface AuthContextType {
  user: UserInfo | null;
  isAuthenticated: boolean;
  userId: string | null;
  username: string | null;
  userRole: string | null;
  avatar?: string | null;
  communityRoles: Record<string, 'member' | 'admin' | 'leader'> | null;
  realNameVerified?: boolean; // 实名认证状态
  organizations?: Array<{
    id: string;
    name: string;
    isPrimary: boolean;
    status: 'active' | 'pending' | 'suspended';
    logo?: string;
    shortName?: string;
  }>;
  currentOrganization?: {
    id: string;
    name: string;
    isPrimary: boolean;
    status: 'active' | 'pending' | 'suspended' | 'approved' | 'rejected' | 'not_submitted';
    logo?: string;
    shortName?: string;
    merchantId?: string;
  };
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  register: (username: string, email: string, password: string) => Promise<boolean>;
  updateUserInfo: (info: Partial<UserInfo>) => void;
  setAuthenticated: (username: string, userId: string, role: UserRole, token: string, isVerified: boolean, nickname?: string, email?: string, phone?: string, avatar?: string) => void;
  setCurrentOrganization?: (org: any) => void;
  isCommunityLeader?: boolean;
}

// 创建上下文
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// 上下文提供者组件
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // 从本地存储加载用户信息
  const loadUserFromStorage = (): UserInfo | null => {
    try {
      const storedUser = localStorage.getItem('auth');
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        // 确保返回的对象结构完整
        return {
          id: parsedUser.id || '',
          username: parsedUser.username || '',
          email: parsedUser.email || '',
          role: parsedUser.role || 'user',
          isAuthenticated: true,
          realNameVerified: parsedUser.realNameVerified || false,
          avatar: parsedUser.avatar,
          wechatOpenid: parsedUser.wechatOpenid,
          communityRoles: parsedUser.communityRoles,
          organizations: parsedUser.organizations,
          currentOrganization: parsedUser.currentOrganization
        };
      }
      return null;
    } catch (error) {
      console.error('加载用户信息失败:', error);
      return null;
    }
  };
  
  // 保存用户信息到本地存储
  const saveUserToStorage = (user: UserInfo | null): void => {
    try {
      if (user) {
        localStorage.setItem('auth', JSON.stringify(user));
      } else {
        localStorage.removeItem('auth');
      }
    } catch (error) {
      console.error('保存用户信息失败:', error);
    }
  };
  
  // 状态管理
  const [user, setUser] = useState<UserInfo | null>(loadUserFromStorage());
  
  // 已有 auth 但无 authToken 时补写（如之前通过 mock 登录的会话），保证 API 请求能带 Authorization
  useEffect(() => {
    if (user && !localStorage.getItem('authToken') && !sessionStorage.getItem('authToken')) {
      localStorage.setItem('authToken', 'mock-token-' + Date.now());
    }
  }, [user]);
  
  // 派生状态
  const isAuthenticated = !!user;
  const userId = user?.id || null;
  const username = user?.username || null;
  const userRole = user?.role || null;
  const communityRoles = user?.communityRoles || null;
  const realNameVerified = user?.realNameVerified || false;
  const organizations = user?.organizations;
  const currentOrganization = user?.currentOrganization;
  
  // 登录方法
  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      // 使用真实API登录
      const response = await userAPI.adminLogin({
        username: username,
        password: password,
        loginType: 'password'
      });

      if (response.code === 200 && response.data) {
        // 登录成功
        const userData = response.data.user || response.data;
        const token = response.data.token || response.data.access_token;

        const mockUser: UserInfo = {
          id: userData.userId || userData.id || 'user_' + Date.now(),
          username: userData.username || username,
          email: userData.email || `${username}@example.com`,
          role: userData.role || 'user' as UserRole,
          isAuthenticated: true,
          realNameVerified: userData.realNameVerified || false,
          avatar: userData.avatar || `https://space.coze.cn/api/coze_space/gen_image?image_size=square&prompt=user+avatar+${username}&sign=${Math.random().toString(36).substring(2, 10)}`,
          wechatOpenid: userData.wechatOpenid,
          communityRoles: userData.communityRoles || {
            'surname-zhang': 'member',
            'friendship-family': 'member',
            'tech-community-1': 'member'
          },
          organizations: userData.organizations,
          currentOrganization: userData.currentOrganization
        };

        setUser(mockUser);
        saveUserToStorage(mockUser);

        // 保存token
        if (token) {
          localStorage.setItem('authToken', token);
        }

        return true;
      } else {
        console.error('登录失败:', response.message);
        return false;
      }
    } catch (error) {
      console.error('登录失败:', error);
      return false;
    }
  };
  
  // 登出方法
  const logout = (): void => {
    setUser(null);
    saveUserToStorage(null);
    // 清除其他相关的本地存储数据
    localStorage.removeItem('pendingJoinCommunityId');
    localStorage.removeItem('selectedJobPosting');
    localStorage.removeItem('authToken');
  };
  
  // 注册方法
  const register = async (username: string, email: string, password: string): Promise<boolean> => {
    try {
      // 模拟API请求延迟
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // 模拟注册成功（实际应用中应调用真实API）
      const mockUser: UserInfo = {
        id: 'user_' + Date.now(),
        username,
        email,
        role: 'user' as UserRole,
        isAuthenticated: true,
        realNameVerified: false,
        avatar: `https://space.coze.cn/api/coze_space/gen_image?image_size=square&prompt=user+avatar+${username}&sign=${Math.random().toString(36).substring(2, 10)}`,
        communityRoles: {}
      };
      
      setUser(mockUser);
      saveUserToStorage(mockUser);
      // mock 注册也写入 authToken，保证 API 请求能带上 Authorization
      const mockToken = 'mock-token-' + Date.now();
      localStorage.setItem('authToken', mockToken);
      
      return true;
    } catch (error) {
      console.error('注册失败:', error);
      return false;
    }
  };
  
  // 更新用户信息方法
  const updateUserInfo = (info: Partial<UserInfo>): void => {
    if (user) {
      const updatedUser = { ...user, ...info };
      setUser(updatedUser);
      saveUserToStorage(updatedUser);
    }
  };
  
    // 设置认证状态方法
    const setAuthenticated = (
    username: string, 
    userId: string, 
    role: UserRole, 
    token: string,
    isVerified: boolean = false,
    nickname?: string,
    email?: string,
    phone?: string,
    avatar?: string
  ): void => {
    try {
      // 构建用户信息对象
      const authenticatedUser: UserInfo = {
        id: userId,
        username: username,
        email: email || `${username}@example.com`,
        phone: phone,
        role: role,
        nickname: nickname,
        isAuthenticated: true,
        realNameVerified: isVerified,
        avatar: avatar || `https://space.coze.cn/api/coze_space/gen_image?image_size=square&prompt=user+avatar+${username}&sign=${Math.random().toString(36).substring(2, 10)}`,
        communityRoles: {},
        // 确保organizations和currentOrganization有默认值，防止undefined问题
         organizations: [
          {
            id: 'org1',
            name: '指点地球村商户',
            isPrimary: true,
            status: 'active',
            shortName: '指点地球村'
          },
          {
            id: 'org2',
            name: '地球村科技有限公司',
            isPrimary: false,
            status: 'active',
            shortName: '地球村科技'
          }
        ],
        currentOrganization: {
          id: 'org1',
          name: '指点地球村商户',
          isPrimary: true,
          status: 'active',
          shortName: '指点地球村'
        }
      };
      
      // 设置用户状态
      setUser(authenticatedUser);
      saveUserToStorage(authenticatedUser);
      
      // 保存token到单独的存储项
      if (token) {
        localStorage.setItem('authToken', token);
      }
      
      console.log('认证状态已设置:', authenticatedUser);
    } catch (error) {
      console.error('设置认证状态失败:', error);
    }
  };
  
  // 设置当前机构
  const setCurrentOrganization = (org: any): void => {
    if (user) {
      const updatedUser = { 
        ...user, 
        currentOrganization: org
      };
      setUser(updatedUser);
      saveUserToStorage(updatedUser);
    }
  };
  
  // 检查是否是社区领导人
  const isCommunityLeader: boolean = !!(communityRoles && Object.values(communityRoles).some(role => role === 'leader'));
  
  // 提供的上下文值
  const contextValue: AuthContextType = {
    user,
    isAuthenticated,
    userId,
    username,
    userRole,
    avatar: user?.avatar ?? null,
    communityRoles,
    realNameVerified: realNameVerified ?? false,
    organizations,
    currentOrganization,
    login,
    logout,
    register,
    updateUserInfo,
    setAuthenticated,
    setCurrentOrganization,
    isCommunityLeader
  };
  
  // 使用React.createElement代替JSX语法
  return React.createElement(
    AuthContext.Provider,
    { value: contextValue },
    children
  );
};

// 自定义钩子，方便使用上下文
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}